import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';

import { describeUnknownError, reportClientError } from '../features/clientErrors';

type AppErrorBoundaryProps = {
  children: React.ReactNode;
};

type AppErrorBoundaryState = {
  hasError: boolean;
};

export class AppErrorBoundary extends React.Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    Sentry.withScope((scope) => {
      scope.setContext('react', { componentStack: errorInfo.componentStack ?? '' });
      Sentry.captureException(error);
    });

    void reportClientError({
      source: 'error-boundary',
      ...describeUnknownError(error),
      componentStack: errorInfo.componentStack,
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          color: 'text.primary',
          display: 'grid',
          placeItems: 'center',
          px: 2,
        }}
      >
        <Stack
          spacing={2}
          sx={{
            width: '100%',
            maxWidth: 480,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper',
            p: 3,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: '1.25rem', fontWeight: 900, mb: 0.5 }}>
              Something went wrong
            </Typography>
            <Typography sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
              We logged the crash so it can be investigated. Reload the app to keep going.
            </Typography>
          </Box>
          <Button variant="contained" onClick={this.handleReload} sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 800 }}>
            Reload app
          </Button>
        </Stack>
      </Box>
    );
  }
}

export function ClientErrorListeners() {
  React.useEffect(() => {
    function handleError(event: ErrorEvent) {
      void reportClientError({
        source: 'window-error',
        ...describeUnknownError(event.error ?? event.message),
        metadata: {
          fileName: event.filename || null,
          lineNumber: event.lineno || null,
          columnNumber: event.colno || null,
        },
      });
    }

    function handleUnhandledRejection(event: PromiseRejectionEvent) {
      void reportClientError({
        source: 'unhandled-rejection',
        ...describeUnknownError(event.reason),
      });
    }

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return null;
}
